import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from 'react';
import { useRouter } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, SidebarSimple, WifiSlash } from '@phosphor-icons/react';
import { IconButton } from '@kiipu/ui';
import { desktopNavigationTracker } from '../lib/desktop-navigation';
import { desktopBridge, platformState, usePlatform } from '../lib/client-platform';
import {
  clampDesktopWidth,
  desktopLayoutKey,
  readDesktopLayout,
  type DesktopLayout,
} from '../lib/desktop-layout';

export function useDesktopLayout(user: string) {
  const key = desktopLayoutKey(platformState()?.apiOrigin || location.origin, user);
  const [record, setRecord] = useState(() => ({ key, value: readDesktopLayout(key) }));
  const layout = record.key === key ? record.value : readDesktopLayout(key);
  function update(patch: Partial<DesktopLayout>) {
    setRecord((previous) => {
      const value = {
        ...(previous.key === key ? previous.value : readDesktopLayout(key)),
        ...patch,
      };
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        /* The current session remains usable. */
      }
      return { key, value };
    });
  }
  return { layout, update };
}

export function DesktopNavigation({ expanded, toggle }: { expanded: boolean; toggle: () => void }) {
  const router = useRouter();
  const history = desktopNavigationTracker(router.history);
  const { canGoBack, canGoForward } = useSyncExternalStore(history.subscribe, history.getSnapshot);
  return (
    <div className="desktop-navigation" role="group" aria-label="窗口导航">
      <IconButton
        label="切换侧栏"
        aria-controls="workspace-navigation"
        aria-expanded={expanded}
        onClick={toggle}
      >
        <SidebarSimple size={17} />
      </IconButton>
      <IconButton label="后退" disabled={!canGoBack} onClick={() => router.history.back()}>
        <ArrowLeft size={17} />
      </IconButton>
      <IconButton label="前进" disabled={!canGoForward} onClick={() => router.history.forward()}>
        <ArrowRight size={17} />
      </IconButton>
    </div>
  );
}

export function DesktopConnectionStatus({ compact = false }: { compact?: boolean }) {
  const state = usePlatform();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!state?.user || state.online) return null;
  return (
    <div className={`desktop-connection${compact ? ' desktop-connection-compact' : ''}`}>
      <button
        type="button"
        disabled={busy}
        aria-busy={busy || undefined}
        title={error || '离线阅读，点击重试连接'}
        onClick={() => {
          setBusy(true);
          setError('');
          void desktopBridge()
            ?.reconnect()
            .catch((cause: Error) => setError(cause.message))
            .finally(() => setBusy(false));
        }}
      >
        <WifiSlash size={15} />
        <span>{busy ? '正在连接…' : '离线阅读'}</span>
      </button>
      <span className="sr-only" role="status">
        {error || '离线阅读'}
      </span>
    </div>
  );
}

export function DesktopResizeHandle({
  kind,
  value,
  maximum,
  onChange,
}: {
  kind: 'sidebar' | 'list';
  value: number;
  maximum: number;
  onChange: (value: number) => void;
}) {
  const drag = useRef<{ x: number; value: number; pointer: number } | null>(null);
  const frame = useRef(0);
  const pending = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const latest = useRef(onChange);
  latest.current = onChange;
  const finish = () => {
    cancelAnimationFrame(frame.current);
    if (pending.current !== null) latest.current(pending.current);
    pending.current = null;
    drag.current = null;
    setDragging(false);
    delete document.documentElement.dataset.desktopResizing;
  };
  useEffect(
    () => () => {
      cancelAnimationFrame(frame.current);
      delete document.documentElement.dataset.desktopResizing;
    },
    [],
  );
  return (
    <div
      className={`desktop-resizer desktop-resizer-${kind}`}
      role="separator"
      tabIndex={0}
      aria-label={kind === 'sidebar' ? '调整侧栏宽度' : '调整内容列表宽度'}
      aria-orientation="vertical"
      aria-valuenow={value}
      aria-valuemin={kind === 'sidebar' ? 190 : 300}
      aria-valuemax={maximum}
      data-dragging={dragging || undefined}
      onDoubleClick={() => onChange(kind === 'sidebar' ? 220 : 480)}
      onKeyDown={(event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        onChange(
          clampDesktopWidth(
            kind,
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? maximum
                : value + (event.key === 'ArrowLeft' ? -20 : 20),
            maximum,
          ),
        );
      }}
      onPointerDown={(event) => {
        if (event.button !== 0 || drag.current) return;
        drag.current = { x: event.clientX, value, pointer: event.pointerId };
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
        document.documentElement.dataset.desktopResizing = 'true';
        event.preventDefault();
      }}
      onPointerMove={(event) => {
        if (drag.current?.pointer !== event.pointerId) return;
        pending.current = clampDesktopWidth(
          kind,
          drag.current.value + event.clientX - drag.current.x,
          maximum,
        );
        cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(() => {
          if (pending.current !== null) latest.current(pending.current);
          pending.current = null;
        });
      }}
      onPointerUp={(event) => {
        finish();
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={finish}
      onLostPointerCapture={finish}
    />
  );
}

export const desktopWorkspaceStyle = (sidebar: number, list: number): CSSProperties =>
  ({
    '--desktop-sidebar-width': `${sidebar}px`,
    '--list-width': `${list}px`,
  }) as CSSProperties;
